import React, { useCallback, useEffect, useMemo, useState } from "react";

import dayjs from "dayjs";
import qs from "query-string";

import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import useToggle from "react-use/lib/useToggle";
import { useTranslation } from "react-i18next";

import { useMutation, useQuery } from "@apollo/client";
import { useRouteMatch, useHistory, useLocation, Link } from "react-router-dom";

import { QUERY_CLIENT_WITH_OUTLOOK_SUBSCRIPTION } from "../../../../config/graphql/query";
import { DELETE_OUTLOOK_SUBSCRIPTION } from "../../../../config/graphql/mutation";

import Table from "../../../../components/Table";
import ConnectAppOnlyModal from "./ConnectAppOnlyModal";

import { readConsentDraft, IOutlookConsentDraft } from "./consentDraft";

import { useClientId } from "../hooks/useClient";

import { getApiUrl } from "../../../../lib/common/getApiUrl";

const TableRow = ({ item }: { item: IClientOutlookSubscription }) => {
  const { t } = useTranslation(["client", "common"]);

  const { url } = useRouteMatch();

  const { id, subscriptionId, createdAt, expirationDateTime, authMode } = item;

  // Everything connected before app-only existed carries no value at all, and
  // the server resolves that to delegated — so read it the same way here.
  const isAppOnly = authMode === "appOnly";

  const [visible, setVisible] = useToggle(false);

  const [onDelete] = useMutation(DELETE_OUTLOOK_SUBSCRIPTION, {
    variables: { id },
    refetchQueries: [
      {
        query: QUERY_CLIENT_WITH_OUTLOOK_SUBSCRIPTION,
        variables: {
          id: item?.client?.id,
        },
      },
    ],
  });

  const onBeforeDelete = () => {
    onDelete();

    return setVisible(false);
  };

  return (
    <>
      <tr>
        <td>{subscriptionId}</td>
        <td>{item?.email}</td>
        <td>
          <span
            className={`badge badge-${isAppOnly ? "success" : "secondary"}`}
          >
            {t(
              `client:client.integrations.outlook.mode.${
                isAppOnly ? "appOnly" : "delegated"
              }`,
            )}
          </span>
        </td>
        <td>{dayjs(createdAt).format("L LT")}</td>
        <td>{dayjs(expirationDateTime).format("L LT")}</td>
        <td>
          <div className="d-flex">
            <Button
              size="sm"
              variant="danger"
              className="mr-2"
              onClick={() => setVisible(true)}
            >
              {t("common:delete")}
            </Button>
            <Link to={`${url}/${id}`}>
              <Button size="sm" variant="primary">
                {t("common:view")}
              </Button>
            </Link>
          </div>
        </td>
      </tr>
      <Modal
        size="sm"
        show={visible}
        onHide={setVisible}
        centered
        aria-labelledby="contained-modal-title-vcenter"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {t("client:client.integrations.outlook.modal.title")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t("client:client.integrations.outlook.modal.body", {
            id,
            subscriptionId,
          })}
        </Modal.Body>
        <Modal.Footer>
          <Button size="sm" onClick={() => setVisible(false)}>
            {t("common:cancel")}
          </Button>
          <Button size="sm" variant="danger" onClick={onBeforeDelete}>
            {t("common:delete")}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

const OutlookIntegrationsRoute = React.memo(() => {
  const clientId = useClientId();

  const { t } = useTranslation(["client", "common"]);

  const [connectVisible, setConnectVisible] = useToggle(false);

  const history = useHistory();
  const location = useLocation();

  const [consentReturn, setConsentReturn] =
    useState<IOutlookConsentDraft | null>(null);
  const [consentGranted, setConsentGranted] = useState(false);
  const [consentError, setConsentError] = useState<string | null>(null);

  // The admin consents in another tab and lands back here on a fresh page, so
  // the finished half of the job lives in the query string and in the draft
  // rather than in component state.
  useEffect(() => {
    const params = qs.parse(location.search);

    const granted = params.outlookAdminConsent === "granted";
    const refused = typeof params.error === "string" ? params.error : null;
    const draft = readConsentDraft(clientId as string);

    if (!granted && !(refused && draft)) {
      return;
    }

    setConsentReturn({
      tenantId: (params.tenantId as string) || draft?.tenantId,
      mailbox: draft?.mailbox,
      title: draft?.title,
    });
    setConsentGranted(granted);
    setConsentError(refused);
    setConnectVisible(true);

    history.replace(location.pathname);
  }, [
    location.search,
    location.pathname,
    clientId,
    history,
    setConnectVisible,
  ]);

  const { data } = useQuery(QUERY_CLIENT_WITH_OUTLOOK_SUBSCRIPTION, {
    fetchPolicy: "network-only",
    skip: !clientId,
    variables: {
      id: clientId,
    },
  });

  const items = data?.client?.outlookSubscriptions ?? [];

  const renderItem = useCallback(
    (item: IClientOutlookSubscription) => (
      <TableRow key={item.id} item={item} />
    ),
    [],
  );

  const apiUrl = getApiUrl();

  const integrationOAuthURL = useMemo(
    () =>
      qs.stringifyUrl(
        {
          url: `${apiUrl}/api/v1/outlook/auth/login`,
          query: {
            client: clientId,
            redirectUri: window.location.href,
          },
        },
        { encode: true },
      ),
    [clientId, apiUrl],
  );

  return (
    <div className="container-fluid">
      {/* Two standalone cards rather than one panel with a heading: each card
          carries its own name, its own consequence and its own button, so the
          choice reads without a question above it. `d-flex` on the column plus
          `mt-auto` on the button keeps both buttons on one line however long the
          two texts turn out in a given language. */}
      <div className="row my-4">
        <div className="col-md-6 d-flex mb-3 mb-md-0">
          <div className="card w-100">
            <div className="card-body d-flex flex-column">
              <h6 className="card-title mb-2">
                {t("client:client.integrations.outlook.help.appOnlyTitle")}
              </h6>
              <small className="text-muted">
                {t("client:client.integrations.outlook.help.appOnlyText")}
              </small>
              <small className="d-block mt-3">
                <strong>
                  {t("client:client.integrations.outlook.help.appOnlyNeeds")}
                </strong>
              </small>
              <div className="mt-auto pt-3">
                <Button
                  variant="primary"
                  onClick={() => setConnectVisible(true)}
                >
                  {t(
                    "client:client.integrations.outlook.button.connectAppOnly",
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-6 d-flex">
          <div className="card w-100">
            <div className="card-body d-flex flex-column">
              <h6 className="card-title mb-2">
                {t("client:client.integrations.outlook.help.delegatedTitle")}
              </h6>
              <small className="text-muted">
                {t("client:client.integrations.outlook.help.delegatedText")}{" "}
                <span className="text-danger">
                  {t(
                    "client:client.integrations.outlook.help.delegatedWarning",
                  )}
                </span>
              </small>
              <small className="d-block mt-3">
                <strong>
                  {t("client:client.integrations.outlook.help.delegatedNeeds")}
                </strong>
              </small>
              <div className="mt-auto pt-3">
                <a
                  href={integrationOAuthURL}
                  className="btn btn-outline-primary"
                >
                  {t("client:client.integrations.outlook.button.generate")}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
      <ConnectAppOnlyModal
        clientId={clientId as string}
        visible={connectVisible}
        onHide={() => setConnectVisible(false)}
        initial={consentReturn}
        consentGranted={consentGranted}
        consentError={consentError}
      />
      <>
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th scope="col">
                {t("client:client.integrations.outlook.th.subscription")}
              </th>
              <th scope="col">
                {t("client:client.integrations.outlook.th.email")}
              </th>
              <th scope="col">
                {t("client:client.integrations.outlook.th.mode")}
              </th>
              <th scope="col">
                {t("client:client.integrations.outlook.th.created")}
              </th>
              <th scope="col">
                {t("client:client.integrations.outlook.th.expires")}
              </th>
              <th scope="col">
                {t("client:client.integrations.outlook.th.actions")}
              </th>
            </tr>
          </thead>
          <tbody>{items.map(renderItem)}</tbody>
        </Table>
      </>
    </div>
  );
});

export default OutlookIntegrationsRoute;
